import React from 'react';

interface ProcessingStatusProps {
  status: 'idle' | 'uploading' | 'generating' | 'processing' | 'complete' | 'error';
  progress: number;
  message?: string;
  error?: string | null;
}

export const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  status,
  progress,
  message,
  error,
}) => {
  if (status === 'idle') {
    return null;
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return '⏳';
      case 'generating':
        return '🤖';
      case 'processing':
        return '⚙️';
      case 'complete':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'complete':
        return 'var(--tl4k-success-600)';
      case 'error':
        return '#dc3545';
      default:
        return '#007bff';
    }
  };

  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--tl4k-spacing-xl)',
        backgroundColor: 'var(--tl4k-surface-warm)',
        borderRadius: 'var(--tl4k-border-radius-md)',
        border: `2px solid ${getStatusColor()}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '32px', marginRight: '12px' }}>
          {getStatusIcon()}
        </span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: getStatusColor() }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </div>
          {message && (
            <div style={{ fontSize: '14px', color: 'var(--tl4k-neutral-600)', marginTop: '4px' }}>
              {message}
            </div>
          )}
        </div>
      </div>

      {status !== 'error' && status !== 'complete' && (
        <div style={{ marginBottom: '12px' }}>
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: 'var(--tl4k-surface-warm)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: getStatusColor(),
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--tl4k-neutral-600)', marginTop: '4px' }}>
            {Math.round(progress)}%
          </div>
        </div>
      )}

      {status === 'generating' && (
        <div style={{ fontSize: '13px', color: 'var(--tl4k-neutral-600)', lineHeight: '1.6' }}>
          <div>✨ AI is generating your 3D model...</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>
            This typically takes 30-60 seconds
          </div>
        </div>
      )}

      {status === 'processing' && (
        <div style={{ fontSize: '13px', color: 'var(--tl4k-neutral-600)' }}>
          Extracting edges for coloring page...
        </div>
      )}

      {status === 'complete' && (
        <div style={{ fontSize: '14px', color: 'var(--tl4k-success-600)' }}>
          🎉 Your 3D model and coloring page are ready!
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: 'var(--tl4k-surface-warm)',
            borderRadius: '4px',
            color: 'var(--tl4k-error-700)',
            fontSize: '14px',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default ProcessingStatus;
